"use client";

export default function TermsAndConditions() {
  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "scope-of-services", title: "Scope of Services" },
    { id: "product-offerings", title: "Product Offerings" },
    { id: "user-responsibilities", title: "User Responsibilities" },
    { id: "payment-terms", title: "Payment Terms" },
    { id: "shipping-and-delivery", title: "Shipping and Delivery" },
    { id: "returns-and-refunds", title: "Returns and Refunds" },
    { id: "intellectual-property", title: "Intellectual Property Rights" },
    { id: "disclaimer", title: "Disclaimer of Warranties" },
    { id: "limitation-of-liability", title: "Limitation of Liability" },
    { id: "safety-and-compliance", title: "Safety and Compliance" },
    { id: "governing-law", title: "Governing Law and Jurisdiction" },
    { id: "modifications", title: "Modifications to Terms" },
    { id: "contact", title: "Contact Information" },
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-8 text-[#0B2A4A]">
          Privacy Policy
        </h1>
        {/* Table of Contents */}
        <div className="bg-white p-6 rounded-lg shadow mb-12">
          <h2 className="text-2xl font-semibold mb-4">Table of Contents</h2>
          <ul className="list-disc list-inside space-y-2">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-blue-600 hover:underline"
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="bg-white p-8 rounded-lg shadow">
              <h2 className="text-3xl font-semibold mb-6 text-[#0B2A4A]">
                {section.title}
              </h2>
              <p className="text-gray-700 leading-relaxed">
                {getTermsContent(section.id)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function getTermsContent(id) {
  switch (id) {
    case "introduction":
      return `
        Effective Date: [Insert Date]
        
        Welcome to Interpetro Chemicals Trading FZ-LLC ("Company", "we", "us", "our"). Please read these Terms and Conditions carefully before accessing or using our website, products, and services. By accessing our website or purchasing our products, you agree to these Terms and Conditions. If you do not agree, you may not use our services.
      `;
    case "scope-of-services":
      return `
        We provide a wide range of specialty chemicals, ingredients, and formulated compounds tailored to meet the requirements of local and regional manufacturers. Our services include:
        
        - Supplying chemicals for industries such as coatings, plastics, construction, agriculture, pharmaceuticals, and more.
        - Offering technical expertise and logistics support.
        - Delivering tailored solutions to optimize manufacturing processes.
      `;
    case "product-offerings":
      return `
        Our products include, but are not limited to:
        
        **Plastics:**
        - PVC Stabilizers (Lead, Tin & Calcium Zinc)
        - Calcium Carbonate (Coated & Grounded)
        - PVC Resins and Reprocessed PVC (Shredded & Powder)
        - HDPE and Reprocessed HDPE
        - Impact Modifiers CPE, PE Wax, Epoxidized Soybean Oil
        - Pigments, Optical Brighteners, Titanium Dioxide
        - Carbon Black, Calcium Oxide, and Antioxidants
        
        **Paints & Coatings:**
        - Additives, Binders, Defoamers
        - Dispersing Agents, Antiskinning Agents
        - Biocides, Driers, PU Curing Agents
        - Thickeners, Solvents, Surfactants
        
        **Polyurethane Chemicals:**
        - TDI (Toluene Diisocyanate), MDI (Methylene Diphenyl Diisocyanate)
        - Polyol, PU Additives, Silicone Surfactants
        - PU Catalysts
        
        **Aluminium Surface Treatment Products:**
        - Degreasing, Etching, Deoxidizing, Brightening Products
        - Anodizing Products, Silk Screen Printing, Colouring
        - Coating and Wastewater Treatment Products
      `;
    case "user-responsibilities":
      return `
        Users of our services must:
        
        - Ensure accurate and complete information during transactions.
        - Use our products in compliance with local laws and regulations.
        - Abide by all applicable safety standards and instructions provided with the products.
      `;
    case "payment-terms":
      return `
        Payments must be made in accordance with the agreed terms outlined in invoices or contracts. Late payments may incur penalties as per local regulations. All prices are subject to change without prior notice, though we will notify customers whenever feasible.
      `;
    case "shipping-and-delivery":
      return `
        Shipping timelines and methods vary based on product and destination. Delays caused by unforeseen circumstances, including customs or logistical issues, are not the responsibility of the Company. Customers are responsible for ensuring proper handling and storage of products upon delivery.
      `;
    case "returns-and-refunds":
      return `
        Returns are accepted only for products damaged during transit or found to be defective, subject to inspection. Refunds are processed once the return is validated and approved by our quality control team.
      `;
    case "intellectual-property":
      return `
        All content, including product descriptions, technical documents, and marketing material, is the intellectual property of Interpetro Chemicals Trading FZ-LLC. Unauthorized use, reproduction, or distribution of our content is strictly prohibited.
      `;
    case "disclaimer":
      return `
        We strive to ensure that all products meet the highest quality standards. However:
        
        - Products are provided "as-is" without warranties of any kind, unless explicitly stated.
        - We disclaim all liability for any misuse or mishandling of the products.
      `;
    case "limitation-of-liability":
      return `
        Interpetro Chemicals Trading FZ-LLC will not be liable for:
        
        - Any indirect, incidental, or consequential damages arising from the use of our products or services.
        - Any damages resulting from improper use, storage, or handling of our products.
      `;
    case "safety-and-compliance":
      return `
        Customers are responsible for ensuring the proper and safe use of chemicals as per industry standards. Compliance with local and international regulations is mandatory for all users of our products.
      `;
    case "governing-law":
      return `
        These Terms shall be governed by and construed in accordance with the laws of the UAE. Any disputes arising from these Terms or your use of our products and services shall be subject to the exclusive jurisdiction of courts in the UAE.
      `;
    case "modifications":
      return `
        We reserve the right to update these Terms and Conditions at any time. Changes will be effective immediately upon posting on our website.
      `;
    case "contact":
      return `
        For any inquiries, please contact:
        
        - **Interpetro Chemicals Trading FZ-LLC**
        - Head Office: Ras Al Khaimah, UAE
        - Email: [Insert Email Address]
        - Phone: [Insert Contact Number]
      `;
    default:
      return "Section content not available.";
  }
}
